import React, { useEffect, useState, useCallback } from 'react';
import { Loader2, ArrowLeft, Paperclip, ExternalLink, RefreshCw, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import api from '../utils/api';
import { openPrivateFile } from '../utils/privateFile';

const STATUS_UI = {
  open: { label: 'Abierto', cls: 'bg-blue-500/15 text-blue-400 border-blue-500/30' },
  in_progress: { label: 'En progreso', cls: 'bg-indigo-500/15 text-indigo-400 border-indigo-500/30' },
  waiting_user: { label: 'Esperando usuario', cls: 'bg-amber-500/15 text-amber-400 border-amber-500/30' },
  resolved: { label: 'Resuelto', cls: 'bg-green-500/15 text-green-400 border-green-500/30' },
  closed: { label: 'Cerrado', cls: 'bg-gray-500/15 text-gray-400 border-gray-500/30' },
};
const STATUS_OPTIONS = ['open', 'in_progress', 'waiting_user', 'resolved', 'closed'];

const fmt = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('es-PE', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};

const AdminSupportPanel = ({ initialTicketId }) => {
  const [tickets, setTickets] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [search, setSearch] = useState('');
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [reply, setReply] = useState('');
  const [replyStatus, setReplyStatus] = useState('waiting_user');
  const [busy, setBusy] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status_filter = statusFilter;
      if (categoryFilter) params.category = categoryFilter;
      if (search.trim()) params.q = search.trim();
      const res = await api.get('/admin/support/tickets', { params });
      setTickets(res.data);
    } catch { toast.error('No se pudieron cargar los tickets'); }
    finally { setLoading(false); }
  }, [statusFilter, categoryFilter, search]);

  const openTicket = useCallback(async (id) => {
    setDetailLoading(true);
    try {
      const res = await api.get(`/admin/support/tickets/${id}`);
      setSelected(res.data);
      setReplyStatus('waiting_user');
    } catch { toast.error('No se pudo abrir el ticket'); }
    finally { setDetailLoading(false); }
  }, []);

  useEffect(() => {
    api.get('/support/meta').then((r) => setCategories(r.data.categories)).catch(() => {});
  }, []);
  useEffect(() => { fetchList(); }, [fetchList]);
  useEffect(() => { if (initialTicketId) openTicket(initialTicketId); }, [initialTicketId, openTicket]);

  const handleReply = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try {
      const res = await api.post(`/admin/support/tickets/${selected.id}/reply`, { body: reply, set_status: replyStatus });
      setSelected(res.data);
      setReply('');
      toast.success('Respuesta enviada');
      fetchList();
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo enviar'); }
    finally { setBusy(false); }
  };

  const handleStatus = async (newStatus) => {
    setBusy(true);
    try {
      await api.put(`/admin/support/tickets/${selected.id}/status`, { status: newStatus });
      await openTicket(selected.id);
      toast.success('Estado actualizado');
      fetchList();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setBusy(false); }
  };

  const viewAttachment = async (aid) => {
    try {
      await openPrivateFile(`/admin/support/attachments/${aid}/file`);
    } catch { toast.error('Adjunto no disponible'); }
  };

  // ── Detail view ──
  if (selected) {
    const su = STATUS_UI[selected.status] || STATUS_UI.open;
    const L = selected.links || {};
    return (
      <div data-testid="admin-support-detail">
        <button onClick={() => { setSelected(null); fetchList(); }} className="flex items-center text-gray-400 hover:text-white mb-4" data-testid="admin-support-back">
          <ArrowLeft className="w-4 h-4 mr-2" /> Volver a la bandeja
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Conversation */}
          <div className="lg:col-span-2">
            <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 mb-4">
              <div className="flex items-start justify-between gap-3 mb-1">
                <h3 className="text-xl font-bold text-white">{selected.subject}</h3>
                <span className={`text-xs font-medium px-3 py-1.5 rounded-full border ${su.cls}`} data-testid="admin-ticket-status">{su.label}</span>
              </div>
              <p className="text-sm text-gray-500">{selected.category_label} · {selected.user?.email}</p>
            </div>

            <div className="space-y-3" data-testid="admin-ticket-messages">
              {(selected.messages || []).map((m) => (
                <div key={m.id} className={`rounded-2xl p-4 border ${m.author_role === 'admin' ? 'bg-[#FF6B35]/10 border-[#FF6B35]/30 ml-6' : 'bg-gray-900 border-gray-800 mr-6'}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="flex items-center gap-2">
                      <span className={`text-xs font-semibold ${m.author_role === 'admin' ? 'text-[#FF6B35]' : 'text-gray-300'}`}>{m.author_role === 'admin' ? 'Soporte Yooin' : 'Usuario'}</span>
                      {m.source === 'email' && <span className="text-[10px] uppercase tracking-wide text-blue-300 border border-blue-500/40 rounded px-1.5" data-testid={`ticket-msg-email-${m.id}`}>vía correo</span>}
                    </span>
                    <span className="text-[11px] text-gray-500">{fmt(m.created_at)}</span>
                  </div>
                  <p className="text-sm text-gray-200 whitespace-pre-wrap">{m.body}</p>
                  {(m.attachments || []).length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {m.attachments.map((a) => (
                        <button key={a.id} disabled={!a.available} onClick={() => viewAttachment(a.id)}
                          className="flex items-center gap-1.5 text-xs bg-gray-800 hover:bg-gray-700 disabled:opacity-40 text-gray-200 px-2.5 py-1.5 rounded-lg" data-testid={`admin-attachment-${a.id}`}>
                          <Paperclip className="w-3.5 h-3.5" /> {a.original_filename || 'adjunto'} {!a.available && '(eliminado)'}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {selected.status !== 'closed' && (
              <div className="bg-gray-900 border border-gray-800 rounded-2xl p-4 mt-4">
                <textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={3}
                  placeholder="Escribe tu respuesta…" data-testid="admin-reply-input"
                  className="w-full bg-black border border-gray-700 rounded-lg p-3 text-sm text-white focus:border-[#FF6B35] outline-none resize-none" />
                <div className="flex items-center justify-between mt-3 gap-3 flex-wrap">
                  <select value={replyStatus} onChange={(e) => setReplyStatus(e.target.value)} data-testid="admin-reply-status"
                    className="bg-black border border-gray-700 rounded-lg px-3 py-2 text-sm text-white">
                    {STATUS_OPTIONS.map((s) => <option key={s} value={s}>Marcar: {STATUS_UI[s].label}</option>)}
                  </select>
                  <Button onClick={handleReply} disabled={busy || !reply.trim()} className="bg-[#FF6B35] hover:bg-[#E55A25] text-white" data-testid="admin-reply-send">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />} Responder
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Investigation sidebar */}
          <div className="space-y-4">
            <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5">
              <h4 className="text-sm font-semibold text-white mb-3">Cambiar estado</h4>
              <div className="flex flex-wrap gap-2">
                {STATUS_OPTIONS.map((s) => (
                  <button key={s} onClick={() => handleStatus(s)} disabled={busy || selected.status === s}
                    className={`text-xs px-3 py-1.5 rounded-full border ${selected.status === s ? STATUS_UI[s].cls : 'border-gray-700 text-gray-400 hover:text-white'}`}
                    data-testid={`admin-set-status-${s}`}>
                    {STATUS_UI[s].label}
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5" data-testid="admin-ticket-links">
              <h4 className="text-sm font-semibold text-white mb-3">Referencias de investigación</h4>
              {(!L.payment_transaction_id && !L.subscription_id && !L.provider_transaction_id && !L.product_id && !L.store_id) ? (
                <p className="text-xs text-gray-500">Sin referencias vinculadas.</p>
              ) : (
                <ul className="text-xs text-gray-300 space-y-2">
                  {L.payment_transaction_id && <li><span className="text-gray-500">Transacción:</span> <code className="text-gray-200">{L.payment_transaction_id}</code></li>}
                  {L.provider_transaction_id && <li><span className="text-gray-500">Culqi charge:</span> <code className="text-gray-200">{L.provider_transaction_id}</code></li>}
                  {L.subscription_id && <li><span className="text-gray-500">Suscripción:</span> <code className="text-gray-200">{L.subscription_id}</code></li>}
                  {L.product_id && <li><span className="text-gray-500">Producto:</span> <code className="text-gray-200">{L.product_id}</code> <ExternalLink onClick={() => window.open(`/producto/${L.product_id}`, '_blank')} className="w-3 h-3 inline cursor-pointer" /></li>}
                  {L.store_id && <li><span className="text-gray-500">Tienda:</span> <code className="text-gray-200">{L.store_id}</code> <ExternalLink onClick={() => window.open(`/vendedor/${L.store_id}`, '_blank')} className="w-3 h-3 inline cursor-pointer" /></li>}
                </ul>
              )}
              <p className="text-[11px] text-gray-600 mt-3">Nunca se almacenan números de tarjeta, CVV ni claves secretas.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Inbox view ──
  return (
    <div data-testid="admin-support-panel">
      <div className="flex flex-wrap items-center gap-2 mb-5">
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="admin-support-status-filter"
          className="bg-gray-900 border border-gray-800 rounded-lg px-3 py-2 text-sm text-white">
          <option value="">Todos los estados</option>
          {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{STATUS_UI[s].label}</option>)}
        </select>
        <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} data-testid="admin-support-category-filter"
          className="bg-gray-900 border border-gray-800 rounded-lg px-3 py-2 text-sm text-white">
          <option value="">Todas las categorías</option>
          {categories.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar por asunto…" data-testid="admin-support-search"
          className="bg-gray-900 border border-gray-800 rounded-lg px-3 py-2 text-sm text-white flex-1 min-w-[180px]" />
        <Button onClick={fetchList} variant="outline" className="border-gray-700 text-white hover:bg-gray-800" data-testid="admin-support-refresh">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-[#FF6B35] animate-spin" /></div>
      ) : tickets.length === 0 ? (
        <p className="text-gray-500 text-sm bg-gray-900 border border-gray-800 rounded-xl p-6" data-testid="admin-support-empty">No hay tickets con estos filtros.</p>
      ) : (
        <div className="space-y-2">
          {tickets.map((t) => {
            const su = STATUS_UI[t.status] || STATUS_UI.open;
            return (
              <button key={t.id} onClick={() => openTicket(t.id)} data-testid={`admin-ticket-${t.id}`}
                className="w-full text-left bg-gray-900 border border-gray-800 hover:border-[#FF6B35]/50 rounded-xl p-4 transition-colors">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-white font-medium truncate">{t.subject}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{t.category_label} · {t.user?.email} · {fmt(t.updated_at)}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {t.last_reply_role === 'user' && t.status !== 'closed' && <span className="w-2 h-2 rounded-full bg-[#FF6B35]" title="Requiere respuesta" />}
                    <span className={`text-xs font-medium px-3 py-1 rounded-full border ${su.cls}`}>{su.label}</span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminSupportPanel;
